'use client'

/**
 * Framer-motion compatibility shim for React 19.
 *
 * framer-motion v11 is NOT compatible with React 19 (used by Next.js 16).
 * This shim replaces framer-motion with lightweight CSS-based alternatives.
 *
 * It exports:
 * - motion: Proxy that maps motion.div, motion.span, etc. to plain elements
 *           with CSS transition support (ignoring framer-motion-specific props)
 * - AnimatePresence: Renders children directly (no exit animations)
 * - useInView: Basic IntersectionObserver hook
 */

import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ElementType,
  type ReactNode,
} from 'react'

// ─── Props filtering ───

const MOTION_PROPS = new Set([
  'initial',
  'animate',
  'exit',
  'transition',
  'variants',
  'whileInView',
  'whileHover',
  'whileTap',
  'whileFocus',
  'whileDrag',
  'viewport',
  'onAnimationStart',
  'onAnimationComplete',
  'layout',
  'layoutId',
  'drag',
  'dragConstraints',
  'dragElastic',
  'dragMomentum',
  'dragTransition',
  'custom',
])

function filterMotionProps(props: Record<string, unknown>): Record<string, unknown> {
  const filtered: Record<string, unknown> = {}
  for (const key of Object.keys(props)) {
    if (!MOTION_PROPS.has(key)) {
      filtered[key] = props[key]
    }
  }
  return filtered
}

// ─── Resolve animated style from initial/animate ───

function resolveStyle(
  initial: Record<string, unknown> | undefined,
  animate: Record<string, unknown> | undefined,
  inlineStyle: React.CSSProperties | undefined
): React.CSSProperties {
  // Apply animate/whileInView values so elements appear in their final (visible) state.
  // We intentionally skip 'initial' to avoid starting invisible.
  const resolved: React.CSSProperties = { ...inlineStyle }

  if (animate && typeof animate === 'object') {
    for (const [key, value] of Object.entries(animate)) {
      if (key === 'opacity') resolved.opacity = value as number
      if (key === 'y') resolved.transform = `translateY(0px)`
      if (key === 'x') resolved.transform = `translateX(0px)`
      if (key === 'scale') resolved.transform = `scale(${value})`
      if (key === 'height') resolved.height = value as string | number
      if (key === 'width') resolved.width = value as string | number
    }
  }

  return resolved
}

// ─── Motion component factory ───

function createMotionComponent(tag: ElementType) {
  const MotionComponent = forwardRef<HTMLElement, Record<string, unknown>>(
    (props, ref) => {
      const { style, initial, animate, whileInView, children, ...rest } = props as {
        style?: React.CSSProperties
        initial?: Record<string, unknown>
        animate?: Record<string, unknown>
        whileInView?: Record<string, unknown>
        children?: ReactNode
        [key: string]: unknown
      }

      const finalStyle = resolveStyle(
        initial as Record<string, unknown> | undefined,
        (animate || whileInView) as Record<string, unknown> | undefined,
        style
      )

      const filtered = filterMotionProps(rest as Record<string, unknown>)

      return React.createElement(
        tag as string,
        { ...filtered, style: finalStyle, ref },
        children
      )
    }
  )

  MotionComponent.displayName = `motion.${String(tag)}`
  return MotionComponent
}

// ─── motion proxy ───

const componentCache = new Map<string, ReturnType<typeof createMotionComponent>>()

// Typé par balise HTML/SVG connue (et non par un index `string`) : avec
// noUncheckedIndexedAccess, `motion.div` valait « composant | undefined » et
// chaque usage était une erreur de type. Aucun effet à l'exécution.
type MotionComponent = ReturnType<typeof createMotionComponent>
export const motion = new Proxy(
  {} as Record<keyof React.JSX.IntrinsicElements, MotionComponent>,
  {
    get(_target, prop: string) {
      if (!componentCache.has(prop)) {
        componentCache.set(prop, createMotionComponent(prop as ElementType))
      }
      return componentCache.get(prop)!
    },
  }
)

// ─── AnimatePresence ───

export function AnimatePresence({
  children,
}: {
  children: ReactNode
  mode?: string
  initial?: boolean
  onExitComplete?: () => void
}) {
  return <>{children}</>
}

// ─── useInView ───

export function useInView(
  ref: React.RefObject<Element | null>,
  options?: { once?: boolean; margin?: string; amount?: number | 'some' | 'all' }
): boolean {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (!ref.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true)
          if (options?.once) {
            observer.disconnect()
          }
        } else if (!options?.once) {
          setInView(false)
        }
      },
      {
        rootMargin: options?.margin || '0px',
        threshold: options?.amount === 'all' ? 1 : options?.amount === 'some' ? 0 : (typeof options?.amount === 'number' ? options.amount : 0),
      }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref, options?.once, options?.margin, options?.amount])

  return inView
}

// ─── Default export (for compatibility) ───

export default { motion, AnimatePresence, useInView }
